import { FunctionComponent, memo, RefObject, useCallback, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  StoryCardPollContentOption,
  StoryCardStatus,
  StoryCardType,
} from '../../../../../services';
import { GalleryItem } from '../../../../models';
import { Button, ButtonType } from '../../../Button';
import { IconLabel } from '../../../IconLabel';
import { IconLabelWithActive } from '../../../IconLabelWithActive';
import { PublishToggle } from '../../../PublishToggle';
import { ScheduleInput } from '../../../ScheduleInput';
import { LinkPostArticleFields, LinkPostQuoteFields, PostType } from '../CreateCardModal';
import { getPostCardValidationError } from '../helpers';

import classes from './CreateCardButtons.module.scss';

interface CreateCardButtonsProps {
  settingsButton: RefObject<HTMLDivElement>;
  settingsOpen: boolean;
  setSettingsOpen: (isOpen: boolean) => void;
  scrollToSettings: () => void;
  renderSettingsToggle: boolean;
  postType: PostType;
  postingTime: Date | null;
  setPostingTime: (postingTime: Date | null) => void;
  cardStatus?: StoryCardStatus;
  shortPostText: string;
  shortPostGallery: GalleryItem[];
  fullPostTitle: string;
  threadPostTitle: string;
  pollPostHeadline: string;
  pollPostOptions: StoryCardPollContentOption[];
  eventId: number | null;
  linkPostType?: StoryCardType;
  linkPostArticle?: LinkPostArticleFields;
  linkPostQuote?: LinkPostQuoteFields;
  isCategoryCard?: boolean;
  editMode: boolean;
  loading: boolean;
  postCard: (status: StoryCardStatus) => Promise<void>;
  onTemplateSave?: () => void;
  isTemplateSaving?: boolean;
}

export const CreateCardButtons: FunctionComponent<CreateCardButtonsProps> = memo(
  ({
    settingsButton,
    settingsOpen,
    setSettingsOpen,
    scrollToSettings,
    renderSettingsToggle,
    postType,
    postingTime,
    setPostingTime,
    cardStatus,
    shortPostText,
    shortPostGallery,
    fullPostTitle,
    threadPostTitle,
    pollPostHeadline,
    pollPostOptions,
    eventId,
    linkPostType,
    linkPostArticle,
    linkPostQuote,
    isCategoryCard,
    editMode,
    loading,
    postCard,
    onTemplateSave,
    isTemplateSaving,
  }) => {
    const { t } = useTranslation();

    const [editCardStatus, setEditCardStatus] = useState<StoryCardStatus | undefined>(cardStatus);

    const isEditCardPublished = useMemo(
      () => editCardStatus === StoryCardStatus.PUBLISHED,
      [editCardStatus]
    );

    const postCardValidationError = useMemo(
      () =>
        getPostCardValidationError({
          postType,
          isCategoryCard,
          shortPostGallery,
          shortPostText,
          fullPostTitle,
          threadPostTitle,
          linkPostType,
          linkPostArticle,
          linkPostQuote,
          pollPostHeadline,
          pollPostOptions,
          eventId,
        }),
      [
        eventId,
        fullPostTitle,
        isCategoryCard,
        linkPostArticle,
        linkPostQuote,
        linkPostType,
        pollPostOptions,
        pollPostHeadline,
        postType,
        shortPostGallery,
        shortPostText,
        threadPostTitle,
      ]
    );

    const postCardHandler = useCallback(
      async (status: StoryCardStatus) => {
        if (postCardValidationError) {
          return toast.error(
            t(`addContent.${isCategoryCard ? 'category' : postType}-post-validation-error`)
          );
        }

        const action = editMode ? 'edit' : 'create';

        await toast.promise(
          postCard(status),
          {
            pending: t(`addContent.${action}-pending`),
            success: t(`addContent.${action}-success`),
            error: t(`addContent.${action}-error`),
          },
          { toastId: 'postCard' }
        );
      },
      [editMode, isCategoryCard, postCard, postCardValidationError, postType, t]
    );

    const templateSaveHandler = useCallback(() => {
      if (postCardValidationError) {
        toast.error(t(`addContent.${postType}-post-validation-error`));
        return;
      }

      onTemplateSave?.();
    }, [onTemplateSave, postCardValidationError, postType, t]);

    const saveButton = useMemo(() => {
      if (editMode) {
        return null;
      }

      const props = {
        onClick: () => postCardHandler(StoryCardStatus.UNPUBLISHED),
        disabled: Boolean(loading || postingTime),
      };

      if (isMobileOnly) {
        return (
          <IconLabel iconId={'save'} iconSize={20} ariaLabel={t('common.save-draft')} {...props} />
        );
      }

      return <Button type={ButtonType.secondary} label={t('common.save-draft')} {...props} />;
    }, [editMode, loading, postCardHandler, postingTime, t]);

    const setEditCardStatusHandler = useCallback(() => {
      setEditCardStatus(
        isEditCardPublished ? StoryCardStatus.UNPUBLISHED : StoryCardStatus.PUBLISHED
      );
    }, [isEditCardPublished]);

    const publishLabel = useMemo(() => {
      switch (true) {
        case editMode:
          return t('common.save');
        case Boolean(postingTime):
          return t('common.schedule');
        case isCategoryCard:
          return t('common.sending');
        default:
          return t('common.publish');
      }
    }, [editMode, isCategoryCard, postingTime, t]);

    const publishClickHandler = useCallback(() => {
      if (!editMode) {
        postCardHandler(postingTime ? StoryCardStatus.SCHEDULED : StoryCardStatus.PUBLISHED);
        return;
      }

      if (isEditCardPublished) {
        postCardHandler(StoryCardStatus.PUBLISHED);
        return;
      }

      postCardHandler(postingTime ? StoryCardStatus.SCHEDULED : StoryCardStatus.UNPUBLISHED);
    }, [editMode, isEditCardPublished, postCardHandler, postingTime]);

    const buttons = useMemo(() => {
      if (onTemplateSave) {
        return (
          <Button
            type={ButtonType.primary}
            label={t(`common.${editMode ? 'save' : 'create'}`)}
            disabled={loading || isTemplateSaving}
            loading={isTemplateSaving}
            onClick={templateSaveHandler}
          />
        );
      }

      return (
        <>
          {(!editMode || editCardStatus !== StoryCardStatus.PUBLISHED) && (
            <ScheduleInput
              time={postingTime}
              setTime={setPostingTime}
              title={t('addContent.schedule-post')}
              subTitle={t('addContent.schedule-post-description')}
              iconHint={t('addContent.schedule-post')}
              summaryLabel={t('schedule.publishing')}
              confirmLabel={t('common.schedule')}
            />
          )}
          {saveButton}
          {editMode && (
            <PublishToggle isPublished={isEditCardPublished} onClick={setEditCardStatusHandler} />
          )}
          <Button
            type={ButtonType.primary}
            label={publishLabel}
            disabled={loading}
            onClick={publishClickHandler}
          />
        </>
      );
    }, [
      editCardStatus,
      editMode,
      isEditCardPublished,
      isTemplateSaving,
      loading,
      onTemplateSave,
      postingTime,
      publishClickHandler,
      publishLabel,
      saveButton,
      setEditCardStatusHandler,
      setPostingTime,
      t,
      templateSaveHandler,
    ]);

    return (
      <div className={classes['buttons']}>
        {renderSettingsToggle && (
          <IconLabelWithActive
            ref={settingsButton}
            iconId={'settings'}
            ariaLabel={t('common.settings')}
            active={settingsOpen}
            setActive={setSettingsOpen}
            onActive={scrollToSettings}
            className={classes['buttons__settings']}
            {...(!isMobileOnly && { label: t('common.settings') })}
          />
        )}
        {buttons}
      </div>
    );
  }
);
